import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  useColorModeValue,
  HStack,
  Image,
} from "@chakra-ui/react";

export default function AIBenefitsSection({ data }) {
  const textColor = useColorModeValue("gray.600", "gray.300");
  const headingColor = useColorModeValue("gray.800", "white");
  const cardBg = useColorModeValue("white", "gray.800");

  // Use benefits from API data, no static fallback
  const benefits = data?.benefits || [];

  // API server URL from environment variable with fallback
  const API_SERVER_URL = process.env.REACT_APP_API_URL;
  console.log("AIBenefitsSection API_SERVER_URL:", API_SERVER_URL);
  
  // Helper function to construct image URL
  const getImageUrl = (imageData) => {
    if (!imageData || !imageData.url) return null;
    
    // If URL is already complete (starts with http), use as-is
    if (imageData.url.startsWith('http')) {
      return imageData.url;
    }
    
    // Otherwise, construct URL with base
    const baseUrl = API_SERVER_URL?.replace('/api', '') || 'http://localhost:1337';
    return `${baseUrl}${imageData.url}`;
  };

  return (
    <Box py={{ base: 12, md: 16 }}>
      <Container maxW="7xl" px={{ base: 4, md: 8 }}>
        {/* Header Section */}
        <VStack
          spacing={{ base: 4, md: 6 }}
          align="flex-start"
          mb={{ base: 8, md: 12 }}
        >
          <Heading
            as="h2"
            fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
            fontWeight="bold"
            color={headingColor}
            lineHeight="shorter"
          >
            {data?.keyBenefit?.title}
          </Heading>
          <Text
            fontSize={{ base: "md", md: "lg" }}
            color={textColor}
            maxW="5xl"
            lineHeight="relaxed"
          >
            {data?.keyBenefit?.description}
          </Text>
        </VStack>

        {/* Benefits Grid */}
        <SimpleGrid
          columns={{ base: 1, md: 2, lg: 4 }}
          spacing={{ base: 6, md: 8 }}
          maxW="full"
        >
          {benefits.map((benefit, index) => (
            <Box
              key={index}
              bg={cardBg}
              p={{ base: 6, md: 8 }}
              borderRadius="lg"
              shadow="sm"
              transition="all 0.3s ease"
              _hover={{
                shadow: "md",
                transform: "translateY(-2px)",
              }}
              border="1px"
              borderColor={"gray.200"}
            >
              <VStack
                spacing={{ base: 4, md: 5 }}
                align="flex-start"
                textAlign="left"
                h="full"
              >
                <HStack>
                  {/* Icon or Checkbox */}
                  <Box
                    w="32px"
                    h="32px"
                    bg="blue.50"
                    borderRadius="full"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                    overflow="hidden"
                  >
                    {benefit.image ? (
                      <Image 
                        src={getImageUrl(benefit.image)}
                        alt={benefit.title}
                        boxSize="20px"
                        objectFit="contain"
                        onLoad={() => console.log(`AIBenefitsSection image loaded: ${benefit.title}`)}
                        onError={(e) => console.log(`AIBenefitsSection image failed: ${benefit.title}`, e.target.src)}
                      />
                    ) : (
                      <Text color="#000B8C" fontSize="sm" fontWeight="bold">
                        ✓
                      </Text>
                    )}
                  </Box>

                  {/* Title */}
                  <Heading
                    as="h3"
                    fontSize={{ base: "lg", md: "xl" }}
                    fontWeight="bold"
                    color={headingColor}
                    lineHeight="tight"
                  >
                    {benefit.title}
                  </Heading>
                </HStack>

                {/* Description */}
                <Text
                  color={textColor}
                  fontSize={{ base: "sm", md: "md" }}
                  lineHeight="relaxed"
                  flex="1"
                >
                  {benefit.description}
                </Text>
              </VStack>
            </Box>
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  );
}
